import React, { createContext, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import type {} from 'redux-thunk/extend-redux';

// Helpers
import { getDesignTokens } from './styles/theme';
import { CookiesProvider, useCookies } from 'react-cookie';
import { sec } from './security';
import { useAuth0 } from '@auth0/auth0-react';

// Components
import HomeView from './views/HomeView';
import DataLibraryView from './views/DataLibraryView';
import ProfileView from './views/ProfileView';
import OnboardingView from './views/OnboardingView';
import MobileView from './views/MobileView';
import IdleWarningDialog from './components/Shared/IdleWarningDialog';
import WorkspaceView from './views/WorkspaceView';
import logo from './assets/img/hde_logo.svg';
import AuditView from './views/AuditView';
import NotFoundView from './views/NotFoundView';

// redux
import { userActions } from './components/User/UserActions';
import { useDispatch, useSelector } from 'react-redux';

// mui
import Grid from '@mui/material/Grid';
import { Box, LinearProgress, PaletteMode, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

function isMobileUser(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );
}

export const ColorModeContext = React.createContext({
    toggleColorMode: () => {
        // This is intentional
    },
});

export const AppContext = createContext(undefined);
export const App = () => {
    const [openIdleDialog, setOpenIdleDialog] = useState(false);
    const [progress, setProgress] = useState(0);
    const dispatch = useDispatch();
    const {
        isLoading,
        isAuthenticated,
        loginWithRedirect,
        getAccessTokenSilently,
        logout,
    } = useAuth0();

    const userDetails = useSelector((state: any) => state.user.currentUser);
    const userConfigDetails = useSelector(
        (state: any) => state.user.currentUserConfig
    );
    const [firstTimeLogin, setFirstTimeLogin] = useState(false);
    const isActuallyLoading = useSelector(
        (state: any) => state.user.currentUserLoading
    );
    const [cookies, setCookies] = useCookies(['hde_mode']);
    const initialModeState: string | (() => string) =
        cookies.hde_mode || 'light';
    const [mode, setMode] = React.useState<PaletteMode | string>(
        initialModeState
    );
    const [loadingComplete, setLoadingComplete] = useState(false);

    if (!isAuthenticated && !isLoading) {
        loginWithRedirect();
    }

    if (isAuthenticated) {
        sec.setAccessTokenSilently(getAccessTokenSilently);
    }

    useEffect(() => {
        if (
            typeof userConfigDetails !== 'undefined' ||
            typeof userDetails !== 'undefined'
        ) {
            if (userDetails?.userConfiguration?.isFirstTime !== undefined) {
                setFirstTimeLogin(userDetails.userConfiguration.isFirstTime);
            }
            if (typeof userConfigDetails !== 'undefined') {
                setFirstTimeLogin(userConfigDetails.isFirstTime); // userConfigDetails starts as undefined as currently there is no way to call a get request for user config so this checks whats in the db and goes from there once userConfig gets updated
            }
        }
    }, [userConfigDetails, userDetails]);

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prevProgress) =>
                prevProgress >= 100
                    ? 100
                    : Math.min(prevProgress + Math.random() * 100, 95)
            );
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    // Step 2
    useEffect(() => {
        if (!isLoading && progress >= 100) {
            setLoadingComplete(true);
        }
    }, [isLoading, progress]);

    useEffect(() => {
        if (
            isAuthenticated &&
            (typeof userDetails === 'undefined' ||
                typeof userDetails.userConfiguration === 'undefined')
        ) {
            dispatch(userActions.getUser());
            dispatch(userActions?.getPicture());
        }
        if (!isLoading) {
            setProgress(100);
        }
    }, [isLoading, dispatch, userDetails, isAuthenticated]);

    const colorMode = React.useMemo(
        () => ({
            // The dark mode switch would invoke this method
            toggleColorMode: () => {
                setMode((prevMode: string) => {
                    const newMode = prevMode === 'light' ? 'dark' : 'light';
                    setCookies('hde_mode', newMode, {
                        path: '/',
                        // domain: 'localhost',
                        domain: 'healthdataexplorer.io',
                    });
                    return newMode;
                });
            },
        }),
        [setCookies]
    );

    // Update the theme only if the mode changes
    const theme = React.useMemo(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        () => createTheme(getDesignTokens(mode)),
        [mode]
    );

    // activate idle timer once user authenticated
    // useEffect(() => {
    //   if (authState.isAuthenticated) {
    //     idleTimer.current.start();
    //   }
    // }, [authState]);
    //
    // const onIdle = (e) => {
    //   setOpenIdleDialog(true);
    // };

    const handleLogout = () => {
        // @ts-ignore
        logout({ returnTo: window.location.origin });
    };

    if (isMobileUser()) {
        return <MobileView />;
    }

    if (isAuthenticated && firstTimeLogin && loadingComplete) {
        return (
            <AppContext.Provider
                value={{
                    userDetails,
                    isActuallyLoading,
                    isAuthenticated,
                    loginWithRedirect,
                }}
            >
                <ThemeProvider theme={theme}>
                    <OnboardingView setFirstTimeLogin={setFirstTimeLogin} />
                </ThemeProvider>
            </AppContext.Provider>
        );
    } else if (
        isAuthenticated &&
        ((userConfigDetails && !userConfigDetails.isFirstTime) ||
            (userDetails &&
                userDetails.userConfiguration &&
                !userDetails.userConfiguration.isFirstTime)) &&
        loadingComplete
    ) {
        return (
            <div className="App">
                <CookiesProvider>
                    <ColorModeContext.Provider value={colorMode}>
                        <ThemeProvider theme={theme}>
                            {/*<IdleTimer*/}
                            {/*  ref={idleTimer}*/}
                            {/*  onIdle={onIdle}*/}
                            {/*  debounce={250}*/}
                            {/*  startOnMount={false}*/}
                            {/*  startManually={true}*/}
                            {/*  timeout={1000 * 60 * 8}*/}
                            {/*/>*/}
                            <IdleWarningDialog
                                open={openIdleDialog}
                                onLogout={() => handleLogout()}
                                onClose={() => setOpenIdleDialog(false)}
                            />
                            <Routes>
                                <Route exact path="/" element={<HomeView />} />
                                <Route
                                    exact
                                    path="/explorer-library"
                                    element={<DataLibraryView />}
                                />
                                <Route
                                    exact
                                    path="/account"
                                    element={<ProfileView />}
                                />
                                <Route
                                    exact
                                    path="/workspace/:id"
                                    element={<WorkspaceView />}
                                />
                                <Route
                                    exact
                                    path="/workspace/:id/activities"
                                    element={<AuditView />}
                                />
                                <Route
                                    exact
                                    path="/activities"
                                    element={<AuditView />}
                                />
                                <Route path="*" element={<NotFoundView />} />
                            </Routes>
                        </ThemeProvider>
                    </ColorModeContext.Provider>
                </CookiesProvider>
            </div>
        );
    } else if (isAuthenticated) {
        //@ts-ignore
        function LinearProgressWithLabel(props) {
            return (
                <Box display="flex" alignItems="center">
                    <Box width={300} mr={1}>
                        <LinearProgress
                            color={'primary'}
                            variant="determinate"
                            {...props}
                        />
                    </Box>
                    <Box minWidth={35}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                        >{`${Math.round(props.value)}%`}</Typography>
                    </Box>
                </Box>
            );
        }

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '80%',
                    margin: '0 auto',
                }}
            >
                <ThemeProvider theme={theme}>
                    <AppContext.Provider
                        value={{
                            userDetails,
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            alignContent={'center'}
                        >
                            <Grid item sx={{ textAlign: 'center' }}>
                                <img
                                    src={logo}
                                    alt=""
                                    style={{ width: '50%' }}
                                    id={'loadingProgress'}
                                />
                            </Grid>
                            <Grid item xs={12} style={{ marginRight: '0%' }}>
                                <LinearProgressWithLabel value={progress} />
                            </Grid>
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Typography color={'primary'}>
                                    Loading... please wait
                                </Typography>
                            </Grid>
                        </Grid>
                    </AppContext.Provider>
                </ThemeProvider>
            </div>
        );
    } else {
        return null;
    }
};

export default App;
